import React, { useCallback, useEffect, useState } from "react";

import { toast } from "react-toastify";
import {
	Loader2,
	CreditCard,
	CheckCircle2,
	XCircle,
	AlertTriangle,
	Copy,
} from "lucide-react";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../components/ui/select";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { getBackendUrl } from "../../config";

const GlobalConfig = () => {
	const [config, setConfig] = useState(null);
	const [carregando, setCarregando] = useState(true);
	const [salvando, setSalvando] = useState(false);

	// Segredos ficam em campos à parte: em branco significa "não mexi",
	// e não "apague o que está gravado".
	const [novaChave, setNovaChave] = useState("");
	const [novoToken, setNovoToken] = useState("");

	const urlWebhook = `${String(getBackendUrl() || "").replace(/\/+$/, "")}/webhooks/asaas`;

	const carregar = useCallback(async () => {
		setCarregando(true);
		try {
			const { data } = await api.get("/global-config");
			setConfig(data);
		} catch (err) {
			toastError(err);
		}
		setCarregando(false);
	}, []);

	useEffect(() => {
		carregar();
	}, [carregar]);

	const salvar = async (e) => {
		e.preventDefault();
		setSalvando(true);
		try {
			const { data } = await api.put("/global-config", {
				asaasEnv: config.asaasEnv,
				trialDays: config.trialDays,
				...(novaChave && { asaasApiKey: novaChave }),
				...(novoToken && { asaasWebhookToken: novoToken }),
			});
			setConfig(data);
			setNovaChave("");
			setNovoToken("");
			toast.success("Configurações salvas.");
		} catch (err) {
			toastError(err);
		}
		setSalvando(false);
	};

	if (carregando || !config) {
		return (
			<MainContainer>
				<MainHeader>
					<Title>Configurações globais</Title>
				</MainHeader>
				<div className="flex-1 px-6 pb-6">
					<Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
				</div>
			</MainContainer>
		);
	}

	return (
		<MainContainer>
			<MainHeader>
				<Title>Configurações globais</Title>
			</MainHeader>

			<div className="flex-1 overflow-auto px-6 pb-6">
				<p className="mb-4 w-full text-sm leading-relaxed text-muted-foreground">
					Ajustes da plataforma inteira, não de uma empresa. Quem mexe aqui é
					só quem opera o sistema — o que muda nesta tela vale para todos os
					seus clientes.
				</p>

				<form onSubmit={salvar} className="w-full space-y-6">
					{/* Gateway */}
					<div className="space-y-4 rounded-xl border bg-card p-5">
						<div className="flex items-center justify-between gap-3">
							<div className="flex items-center gap-2">
								<CreditCard className="h-4 w-4 text-muted-foreground" />
								<h2 className="text-sm font-semibold">Cobrança (Asaas)</h2>
							</div>
							{config.gatewayPronto ? (
								<Badge variant="secondary" className="gap-1">
									<CheckCircle2 className="h-3 w-3" />
									Funcionando em {config.gatewayAmbiente}
								</Badge>
							) : (
								<Badge variant="destructive" className="gap-1">
									<XCircle className="h-3 w-3" />
									Sem chave
								</Badge>
							)}
						</div>

						{!config.gatewayPronto && (
							<p className="flex gap-1.5 rounded-md bg-muted p-3 text-xs leading-relaxed">
								<AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
								Sem a chave, nenhuma fatura vira Pix ou boleto: a tela
								Financeiro dos seus clientes mostra “cobrança em preparo” e
								ninguém consegue pagar.
							</p>
						)}

						<div>
							<Label htmlFor="asaasApiKey">Chave da API</Label>
							<Input
								id="asaasApiKey"
								type="password"
								autoComplete="new-password"
								placeholder={
									config.asaasApiKeyDefinida
										? `Guardada — termina em ${config.asaasApiKeyFinal}`
										: "Cole a chave do painel do Asaas"
								}
								value={novaChave}
								onChange={(e) => setNovaChave(e.target.value)}
							/>
							<p className="mt-1 text-xs leading-relaxed text-muted-foreground">
								{config.asaasApiKeyDefinida &&
									"Deixe em branco para manter a que já está gravada."}
								{config.chaveVemDoEnv &&
									"Hoje a chave em uso vem do arquivo .env do servidor. Se você gravar uma aqui, ela passa a valer no lugar — e trocar o .env deixa de ter efeito."}
								{!config.asaasApiKeyDefinida &&
									!config.chaveVemDoEnv &&
									"Encontre em Configurações → Integrações, no painel do Asaas."}
							</p>
						</div>

						<div>
							<Label htmlFor="asaasEnv">Ambiente</Label>
							<Select
								value={config.asaasEnv}
								onValueChange={(v) => setConfig({ ...config, asaasEnv: v })}
							>
								<SelectTrigger id="asaasEnv">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="sandbox">
										Sandbox — cobranças de teste, ninguém paga de verdade
									</SelectItem>
									<SelectItem value="production">
										Produção — cobranças reais
									</SelectItem>
								</SelectContent>
							</Select>
							<p className="mt-1 text-xs text-muted-foreground">
								A chave é diferente em cada ambiente. Trocar aqui sem trocar a
								chave faz toda cobrança falhar.
							</p>
						</div>

						<div className="border-t pt-4">
							<Label htmlFor="asaasWebhookToken">Token do webhook</Label>
							<Input
								id="asaasWebhookToken"
								type="password"
								autoComplete="new-password"
								placeholder={
									config.asaasWebhookTokenDefinido
										? "Guardado — deixe em branco para manter"
										: "Invente uma senha e repita no painel do Asaas"
								}
								value={novoToken}
								onChange={(e) => setNovoToken(e.target.value)}
							/>
							<p className="mt-1 text-xs leading-relaxed text-muted-foreground">
								É o que impede alguém de marcar faturas como pagas mandando um
								POST. Sem ele, o webhook aceita qualquer chamada.
							</p>

							<div className="mt-2">
								<Label className="text-xs text-muted-foreground">
									Endereço para cadastrar no Asaas
								</Label>
								<div className="mt-1 flex gap-2">
									<Input
										readOnly
										value={urlWebhook}
										className="font-mono text-xs"
										onFocus={(e) => e.target.select()}
									/>
									<Button
										type="button"
										variant="outline"
										size="icon"
										aria-label="Copiar endereço do webhook"
										onClick={async () => {
											try {
												await navigator.clipboard.writeText(urlWebhook);
												toast.success("Endereço copiado.");
											} catch {
												toast.error("Não consegui copiar.");
											}
										}}
									>
										<Copy className="h-4 w-4" />
									</Button>
								</div>
							</div>
						</div>
					</div>

					{/* Teste */}
					<div className="space-y-3 rounded-xl border bg-card p-5">
						<h2 className="text-sm font-semibold">Período de teste</h2>
						<div>
							<Label htmlFor="trialDays">
								Dias de teste para uma empresa nova
							</Label>
							<div className="mt-1 flex items-center gap-2">
								<Input
									id="trialDays"
									type="number"
									min="1"
									className="w-24"
									value={config.trialDays}
									onChange={(e) =>
										setConfig({ ...config, trialDays: e.target.value })
									}
								/>
								<span className="text-sm text-muted-foreground">dias</span>
							</div>
							<p className="mt-1 text-xs leading-relaxed text-muted-foreground">
								Vale quando você inicia um teste sem dizer a quantidade de
								dias. Testes já em andamento não mudam.
							</p>
						</div>
					</div>

					<Button type="submit" disabled={salvando}>
						{salvando && <Loader2 className="h-4 w-4 animate-spin" />}
						Salvar
					</Button>
				</form>
			</div>
		</MainContainer>
	);
};

export default GlobalConfig;
